/* ============================================================
   ORION BUY BAR — the mobile sticky Add to Cart.

   It does not own any purchase logic. It watches the real CTA,
   mirrors its label, price and disabled state, and forwards its
   own click to it. That way subscription plans, flavour choice
   and quantity keep a single source of truth in the section's own
   script, and this bar can never disagree with the button it
   stands in for.

   Both the buybox and the mix-and-match bundle builder use it, so
   the selectors come off the bar element rather than being baked
   in here.
   ============================================================ */
(() => {
  const MOBILE = '(max-width: 749px)';

  function setup(bar) {
    if (bar.dataset.ready === '1') return;
    const root = bar.closest('[data-o-bar-root]') || bar.parentElement;
    const pick = (key, fallback) => root.querySelector(bar.dataset[key] || fallback);

    const cta = pick('ctaSelector', '[data-o-cta]');
    const anchor = pick('anchorSelector', '.o-buy__qty-row');
    const price = pick('priceSelector', '[data-o-price]');
    const name = pick('nameSelector', '[data-o-flavour-name]');
    if (!cta || !anchor) return;

    bar.dataset.ready = '1';
    const barCta = bar.querySelector('[data-o-bar-cta]');
    const barPrice = bar.querySelector('[data-o-bar-price]');
    const barName = bar.querySelector('[data-o-bar-name]');
    const mq = window.matchMedia(MOBILE);

    /* The stepper mirrors the page's own quantity control and forwards its
       taps there, so the section's state stays the single source of truth --
       same contract as the CTA. Sections without a quantity (the packs
       configurator) simply have no stepper in their bar markup. */
    const qtyWrap = bar.querySelector('[data-o-bar-qty]');
    const qtyCount = bar.querySelector('[data-o-bar-count]');
    const pageQty = root.querySelector('[data-o-qty]');
    const pageDec = root.querySelector('[data-o-qty-dec]');
    const pageInc = root.querySelector('[data-o-qty-inc]');
    if (qtyWrap) {
      if (pageDec && pageInc) {
        bar.querySelector('[data-o-bar-dec]').addEventListener('click', () => pageDec.click());
        bar.querySelector('[data-o-bar-inc]').addEventListener('click', () => pageInc.click());
      } else {
        qtyWrap.hidden = true;
      }
    }

    let ctaOffscreen = false;

    const mirror = () => {
      const label = (cta.textContent || '').trim();
      if (label) barCta.textContent = label;
      // Never actually disable the bar's button: a disabled control fires no
      // click event, and the tap needs to reach the scroll-back handler.
      barCta.classList.toggle('is-blocked', cta.disabled);
      barCta.setAttribute('aria-disabled', String(cta.disabled));
      barCta.classList.toggle('is-loading', cta.classList.contains('is-loading'));
      // The price node holds the current price *and* a struck-through compare
      // price as sibling elements. Reading textContent glued them together as
      // "$45.50$65.00", so copy the markup and let CSS space them.
      if (price && barPrice) barPrice.innerHTML = price.innerHTML;
      if (name && barName && name.textContent.trim()) {
        barName.textContent = name.textContent.trim();
      }
      if (qtyCount && pageQty) qtyCount.textContent = pageQty.textContent.trim() || '1';
    };

    // The cart drawer is a modal; leaving the bar mounted underneath keeps
    // body padding applied behind it for no benefit, and the bar would flash
    // over the overlay on slower paints.
    const drawerOpen = () => !!document.querySelector('cart-drawer-component dialog[open]');

    // Deliberately shown even when the CTA is disabled. On the mix-and-match
    // bundle "disabled" means "pick 3 more", which is the single most useful
    // thing to keep on screen once the picker has scrolled away — hiding the
    // bar there left the longest flow on the site with no sticky guidance.
    const paint = () => {
      const show = ctaOffscreen && mq.matches && !drawerOpen();
      bar.hidden = !show;
      document.body.classList.toggle('has-orion-buy-bar', show);
    };

    const drawer = document.querySelector('cart-drawer-component');
    if (drawer) {
      new MutationObserver(paint).observe(drawer, {
        subtree: true,
        attributes: true,
        attributeFilter: ['open'],
      });
    }

    new IntersectionObserver(
      ([entry]) => {
        ctaOffscreen = !entry.isIntersecting;
        paint();
      },
      { rootMargin: '0px 0px -10% 0px' }
    ).observe(anchor);

    // Price and label are rewritten by the section whenever a plan, flavour or
    // bundle slot changes, so follow the button rather than polling.
    new MutationObserver(() => {
      mirror();
      paint();
    }).observe(cta, { childList: true, characterData: true, subtree: true, attributes: true });

    document.addEventListener('orion:variant:changed', mirror);
    mq.addEventListener('change', paint);

    barCta.addEventListener('click', () => {
      // A disabled CTA means something upstream is unfinished, so send them to
      // it rather than swallowing the tap.
      if (cta.disabled) {
        anchor.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      cta.click();
    });

    mirror();
    paint();
  }

  const boot = () => document.querySelectorAll('[data-o-bar]').forEach(setup);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
